import type { ElementType, ReactNode } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';
import type { SpringValue, UseTransitionProps } from '@react-spring/web';
import { useTransition } from '@react-spring/web';

import type { ToastContent } from './types';
import {
  getDismissalTimeFromCharacterLength,
  isDecoratedToastContent,
} from './utilities';

interface PieceOfToast {
  key: string;
  children: ToastContent;
}

interface ToastRackProviderProps {
  children: ReactNode;
  Wrapper: ElementType;
  Notification: ElementType<{
    style: Record<string, SpringValue>;
    children: ToastContent;
  }>;
  characterTimeMultiplier: number;
}

interface Context {
  toasts: PieceOfToast[];
  /** **WARNING:** the ReactNode cannot use `<FormattedMessage />`
   * because the `onlyText` function cannot extract the text
   * from that component.
   */
  addToast: (toast: ToastContent) => void;
}

const ANIM = {
  OUT: { transform: 'translate3d(0,20px,0)', opacity: 0 },
  IN: {
    transform: 'translate3d(0,0px,0)',
    opacity: 1,
  },
};

const springConfig: UseTransitionProps['config'] = (
  _item,
  _index,
  transitionPhase,
) =>
  transitionPhase !== 'leave'
    ? {
        mass: 1,
        tension: 150,
        friction: 18,
        clamp: true,
        velocity: 0.001, // seconds
      }
    : {
        mass: 2,
        tension: 180,
        friction: 5,
        clamp: true,
        velocity: 0.001, // seconds
      };

const toastRackContext = createContext<Context | undefined>(undefined);

const { Provider } = toastRackContext;

/**
 * Retrieve the addToast function and list of toasts from context.
 */
export const useToasty = (): Context => {
  const toastValues = useContext(toastRackContext);

  if (!toastValues) {
    throw new Error(
      'No Toast Rack context, make sure you have wrapped in ToastRackProvider',
    );
  }

  return toastValues;
};

/**
 * Keeps an array of toast notifications in state, arranged oldest to newest.
 * When a new item is added, the character count is calculated and used to
 * set a timeout for auto-dismissal
 */
const useToastRack = (characterTimeMultiplier: number) => {
  const [toasts, setToasts] = useState<PieceOfToast[]>([]);

  return {
    toasts,
    addToast: useCallback(
      (children: ToastContent) => {
        const key = crypto.randomUUID();

        setToasts((currentToasts) => [...currentToasts, { key, children }]);

        window.setTimeout(
          () => {
            setToasts((currentToasts) =>
              currentToasts.filter((toast) => toast.key !== key),
            );
          },
          getDismissalTimeFromCharacterLength(
            children,
            characterTimeMultiplier,
          ),
        );
      },
      [setToasts, characterTimeMultiplier],
    ),
  };
};

export const ToastRackProvider = ({
  children,
  Wrapper,
  Notification,
  characterTimeMultiplier,
}: ToastRackProviderProps) => {
  const { toasts, addToast } = useToastRack(characterTimeMultiplier);

  const transitions = useTransition(toasts, {
    config: springConfig,
    from: ANIM.OUT,
    enter: ANIM.IN,
    leave: ANIM.OUT,
  });

  return (
    <Provider value={{ toasts, addToast }}>
      {children}
      <Wrapper>
        {transitions((style, { children }) => (
          <Notification style={style}>{children}</Notification>
        ))}
      </Wrapper>
    </Provider>
  );
};

export { getDismissalTimeFromCharacterLength, isDecoratedToastContent };

export type { ToastContent };
